import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, GripVertical, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { CreateResourceDialog } from "@/components/canvas/create-resource-dialog";
import { EditResourceDialog } from "@/components/canvas/edit-resource-dialog";
import { labelForRenderType } from "@/components/canvas/render-type-labels";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { orpc } from "@/lib/rpc";

type Resource = {
  id: string;
  title: string;
  renderType: string | null;
  behavior: string;
  sourceUrl: string | null;
  description?: string | null;
};

function ResourceCardInner({
  resource,
  showExternalIcon = true,
}: {
  resource: Resource;
  showExternalIcon?: boolean;
}) {
  const isExternal = resource.behavior === "redirect" && resource.sourceUrl;
  return (
    <Card
      className="p-5 h-full min-h-[140px] flex flex-col gap-3 hover:border-foreground/30 transition-colors cursor-pointer"
      data-testid={`company-card-${resource.id}`}
    >
      <div className="flex items-start gap-2">
        <h3 className="text-base font-medium leading-snug flex-1">{resource.title}</h3>
        {isExternal && showExternalIcon && (
          <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
      </div>
      {resource.description && (
        <p className="text-sm text-muted-foreground line-clamp-3">{resource.description}</p>
      )}
    </Card>
  );
}

function SortableResourceCard({
  resource,
  onEdit,
  onRemove,
}: {
  resource: Resource;
  onEdit: (resource: Resource) => void;
  onRemove: (resource: Resource) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: resource.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col gap-2 h-full"
      data-testid={`company-card-managing-${resource.id}`}
    >
      <div className="relative flex-1">
        <ResourceCardInner resource={resource} showExternalIcon={false} />
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="absolute top-2 right-2 p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted cursor-grab active:cursor-grabbing touch-none"
          aria-label="Arrastar para reordenar"
          data-testid={`company-card-drag-${resource.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => onEdit(resource)}
          data-testid={`company-card-edit-${resource.id}`}
        >
          Editar
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => onRemove(resource)}
          data-testid={`company-card-remove-${resource.id}`}
        >
          Remover
        </Button>
      </div>
    </div>
  );
}

function ReadOnlyResourceCard({ resource }: { resource: Resource }) {
  const isExternal = resource.behavior === "redirect" && resource.sourceUrl;
  if (isExternal) {
    return (
      <a
        href={resource.sourceUrl!}
        target="_blank"
        rel="noreferrer noopener"
        className="block h-full"
      >
        <ResourceCardInner resource={resource} />
      </a>
    );
  }
  return (
    <Link href={`/company/${resource.id}`} className="block h-full">
      <ResourceCardInner resource={resource} />
    </Link>
  );
}

export default function Company() {
  const { isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const sectionsQueryOptions = orpc.companyContent.list.queryOptions();
  const sections = useQuery(sectionsQueryOptions);

  const [managing, setManaging] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState<Resource | null>(null);
  const [editingResourceId, setEditingResourceId] = useState<string | null>(null);

  const availableQueryOptions = orpc.companyContent.listAvailable.queryOptions();
  const availableQuery = useQuery({ ...availableQueryOptions, enabled: pickerOpen });
  const filteredAvailable = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const list = availableQuery.data ?? [];
    if (q.length === 0) return list;
    return list.filter((r) => r.title.toLowerCase().includes(q));
  }, [availableQuery.data, pickerSearch]);

  const linkMutation = useMutation(
    orpc.onboardingResources.moveSection.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: sectionsQueryOptions.queryKey }),
          queryClient.invalidateQueries({ queryKey: availableQueryOptions.queryKey }),
        ]);
        toast({ title: "Conteúdo adicionado ao Handbook" });
        setPickerOpen(false);
      },
      onError: (err) => {
        const message = (err as { message?: string } | null)?.message;
        toast({
          title: "Não foi possível adicionar",
          description: message,
          variant: "destructive",
        });
      },
    }),
  );

  const unlinkMutation = useMutation(
    orpc.onboardingResources.moveSection.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: sectionsQueryOptions.queryKey });
        toast({ title: "Conteúdo removido desta seção" });
        setPendingRemoval(null);
      },
      onError: (err) => {
        const message = (err as { message?: string } | null)?.message;
        toast({
          title: "Não foi possível remover",
          description: message,
          variant: "destructive",
        });
      },
    }),
  );

  const reorderMutation = useMutation(
    orpc.companyContent.reorder.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: sectionsQueryOptions.queryKey });
      },
      onError: (err) => {
        const message = (err as { message?: string } | null)?.message;
        toast({
          title: "Não foi possível reordenar",
          description: message,
          variant: "destructive",
        });
      },
    }),
  );

  function handleEdit(resource: Resource) {
    setEditingResourceId(resource.id);
  }

  function handleConfirmRemove() {
    if (!pendingRemoval) return;
    unlinkMutation.mutate({ resourceId: pendingRemoval.id, companySectionKey: null });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(sectionKey: string, event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const section = sections.data?.find((s) => s.key === sectionKey);
    if (!section) return;
    const oldIndex = section.resources.findIndex((r) => r.id === active.id);
    const newIndex = section.resources.findIndex((r) => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(section.resources, oldIndex, newIndex);
    queryClient.setQueryData(sectionsQueryOptions.queryKey, (prev: typeof sections.data) =>
      prev?.map((s) => (s.key === sectionKey ? { ...s, resources: next } : s)),
    );
    reorderMutation.mutate({
      items: next.map((r, position) => ({ resourceId: r.id, position })),
    });
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-8 py-10 space-y-10">
      <header className="flex items-start justify-between gap-4 pb-2">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold tracking-tight">Handbook</h1>
          <p className="text-sm text-muted-foreground">
            Cultura, políticas, benefícios e contatos.
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            {managing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManaging(false)}
                data-testid="company-exit-manage"
              >
                Sair do modo gestão
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManaging(true)}
                data-testid="company-enter-manage"
              >
                Gerenciar conteúdo
              </Button>
            )}
          </div>
        )}
      </header>

      {sections.isLoading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {sections.error && (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">Não foi possível carregar o conteúdo.</p>
        </Card>
      )}

      {sections.data && sections.data.length === 0 && !managing && (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">
            Nenhum conteúdo institucional publicado ainda.
          </p>
        </Card>
      )}

      {sections.data?.map((section) => (
        <section key={section.key} className="space-y-3">
          {sections.data!.length > 1 && (
            <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {section.label}
            </h2>
          )}
          {managing ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => handleDragEnd(section.key, e)}
            >
              <SortableContext
                items={section.resources.map((r) => r.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
                  {section.resources.map((r) => (
                    <SortableResourceCard
                      key={r.id}
                      resource={r}
                      onEdit={handleEdit}
                      onRemove={(res) => setPendingRemoval(res)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="rounded-lg border border-dashed border-border p-5 min-h-[140px] flex items-center justify-center text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                    data-testid="company-create-card"
                  >
                    + Novo conteúdo
                  </button>
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
              {section.resources.map((r) => (
                <ReadOnlyResourceCard key={r.id} resource={r} />
              ))}
            </div>
          )}
        </section>
      ))}

      {managing && (sections.data?.length ?? 0) === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded-lg border border-dashed border-border p-5 min-h-[140px] flex items-center justify-center text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
            data-testid="company-create-card-empty"
          >
            + Novo conteúdo
          </button>
        </div>
      )}

      <CreateResourceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={async () => {
          await queryClient.invalidateQueries({ queryKey: sectionsQueryOptions.queryKey });
          toast({ title: "Conteúdo criado" });
        }}
      />

      <EditResourceDialog
        open={editingResourceId !== null}
        onOpenChange={(open) => {
          if (!open) setEditingResourceId(null);
        }}
        resourceId={editingResourceId}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: sectionsQueryOptions.queryKey });
        }}
      />

      <Dialog
        open={pickerOpen}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (!open) setPickerSearch("");
        }}
      >
        <DialogContent className="sm:max-w-md p-0 gap-0">
          <DialogHeader className="px-5 pt-5 pb-4 border-b border-border space-y-1">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-primary">
              Adicionar conteúdo
            </p>
            <DialogTitle className="text-lg font-semibold tracking-tight">Handbook</DialogTitle>
            <DialogDescription className="sr-only">
              Reaproveite um recurso do pool ou crie um novo.
            </DialogDescription>
          </DialogHeader>

          <div className="px-5 py-4 border-b border-border space-y-3">
            <Input
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="Buscar recurso por título"
              data-testid="company-picker-search"
            />
            <p className="text-xs text-muted-foreground tabular-nums">
              {filteredAvailable.length === 1
                ? "1 recurso disponível"
                : `${filteredAvailable.length} recursos disponíveis`}
            </p>
          </div>

          <div className="max-h-80 overflow-y-auto min-h-0" data-testid="company-picker-list">
            {availableQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filteredAvailable.length === 0 ? (
              <p className="px-5 py-6 text-sm text-muted-foreground">
                {pickerSearch.trim()
                  ? "Nenhum recurso encontrado."
                  : "Nenhum recurso disponível no pool."}
              </p>
            ) : (
              <ul className="p-2 space-y-1">
                {filteredAvailable.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      disabled={linkMutation.isPending}
                      onClick={() =>
                        linkMutation.mutate({
                          resourceId: r.id,
                          companySectionKey: "company-root",
                        })
                      }
                      className="w-full rounded-lg border border-transparent hover:border-border hover:bg-card transition-colors text-left px-3 py-3 disabled:opacity-50"
                      data-testid={`company-pick-${r.id}`}
                    >
                      <p className="text-sm font-semibold tracking-tight truncate">{r.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {labelForRenderType(r.renderType)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="px-5 py-4 border-t border-border bg-background">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setPickerOpen(false);
                setCreateOpen(true);
              }}
              data-testid="company-picker-create"
            >
              + Novo recurso
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingRemoval}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover conteúdo?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingRemoval?.title}" será removido desta seção mas continuará disponível no pool
              de recursos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlinkMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmRemove();
              }}
              disabled={unlinkMutation.isPending}
              data-testid="company-remove-confirm"
            >
              {unlinkMutation.isPending ? "Removendo..." : "Remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
